import { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { useLayout } from "../../../contexts/UseLayout";
import NotificationsService from "@/services/notificationsService";
import moment from "moment";
import {
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  CheckCheck,
  CircleDot,
  ExternalLink,
  Flag,
  ListFilter,
  MessageCircle,
  Share2,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import Select, { SingleValue } from "react-select";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { TarefaView } from "@/components/MenuUtilidades/Tarefas/components/TarefaView";

const PER_PAGE = 20;
const NOTIFICATION_TYPES = [
  "all",
  "desk",
  "reuniao",
  "shared",
  "recrutamento",
  "feedback",
  "social",
  "engajamento",
] as const;

type NotifType =
  (typeof NOTIFICATION_TYPES)[number];

type CategoryKey =
  | "tarefa"
  | "tarefa_comentario"
  | "reuniao"
  | "recrutamento"
  | "feedback"
  | "social"
  | "engajamento"
  | "shared"
  | "desk_status"
  | "desk_comment"
  | "addesk";

type SelectOption = {
  value: number | string;
  label: string;
};

type UserPreview = {
  id?: number | string;
  name?: string | null;
  last_name?: string | null;
  url_img?: {
    path?: string | null;
  } | null;
};

type NotificationItem = {
  id: number;
  event?: string | null;
  description?: string | null;
  link?: string | null;
  read?: boolean;
  created_at?: string | null;
  tarefa?: {
    id?: number | string;
    title?: string | null;
  } | null;
  addesk?: {
    id?: number | string;
    name?: string | null;
  } | null;
  feedback_report?: {
    id?: number | string;
    title?: string | null;
  } | null;
  post?: {
    id?: number | string;
    creator?: UserPreview | null;
  } | null;
  profile_user?: UserPreview | null;
  candidatura?: {
    id?: number | string;
  } | null;
  vaga?: {
    id?: number | string;
  } | null;
  usuario_send?: UserPreview | null;
};

type GroupedNotificationItem = {
  itemId: string;
  tarefa?: NotificationItem["tarefa"];
  addesk?: NotificationItem["addesk"];
  resolvedDeskId?: number | string | null;
  feedbackReport?: NotificationItem["feedback_report"];
  post?: NotificationItem["post"];
  profileUser?: NotificationItem["profile_user"];
  lastNotification: NotificationItem;
  type: CategoryKey;
};

type NotificationGroups = Record<string, NotificationItem[]>;

type NotificationApiResponse = {
  data?: {
    qtdRegistros?: number;
    data?: NotificationItem[];
  };
};

type InfiniteNotificationData = {
  pages?: NotificationApiResponse[];
};

type UserLogged = {
  data?: {
    is_admin?: boolean;
    is_ad_link?: boolean;
  };
};

type TaskNotificationTarget = {
  id?: number | string;
  [key: string]: unknown;
};

function parseNotificationLink(link?: string | null): unknown {
  if (!link) return null;

  try {
    return JSON.parse(link);
  } catch {
    return link;
  }
}

function isSocialProfileEvent(event?: string | null) {
  return event === "social_new_follower" || event === "social_followers_growth";
}

function isTalentEvent(event?: string | null) {
  return (
    !!event?.startsWith("talent_") ||
    event === "professional_invite_accepted"
  );
}

function isEngagementEvent(event?: string | null) {
  return !!event?.startsWith("engagement_");
}

const TASK_NOTIFICATION_EVENTS = new Set([
  "tarefa_concluida",
  "tarefa_criada",
  "tarefa_em_andamento",
  "tarefa_vencida",
]);

const TASK_NOTIFICATION_LABEL_KEYS: Record<string, string> = {
  tarefa_concluida: "notificationsModule.badges.task_completed",
  tarefa_criada: "notificationsModule.badges.task_created",
  tarefa_em_andamento: "notificationsModule.badges.task_in_progress",
  tarefa_vencida: "notificationsModule.badges.task_overdue",
};

const TASK_COMMENT_NOTIFICATION_EVENTS = new Set(["comentario_tarefa"]);

function isTaskNotificationEvent(event?: string | null) {
  return TASK_NOTIFICATION_EVENTS.has(event ?? "");
}

function isTaskCommentNotificationEvent(event?: string | null) {
  return TASK_COMMENT_NOTIFICATION_EVENTS.has(event ?? "");
}

function isTaskCategory(type: CategoryKey) {
  return type === "tarefa" || type === "tarefa_comentario";
}

function taskNotificationLabel(event: string | null | undefined, t: (key: string) => string) {
  return t(TASK_NOTIFICATION_LABEL_KEYS[event ?? ""] ?? "notificationsModule.badges.tasks");
}

function asUrlParam(value: unknown) {
  if (value === null || value === undefined || value === "") return "";
  return encodeURIComponent(String(value));
}

function getEngagementDestination(event?: string | null, link?: string | null) {
  const parsedLink = parseNotificationLink(link);
  if (typeof parsedLink === "string" && parsedLink.startsWith("/")) {
    return parsedLink;
  }

  const fallbackByEvent: Record<string, string> = {
    engagement_profile_incomplete: "/ad-link/curriculo",
    engagement_profile_weekly_summary: "/ad-link/curriculo",
    engagement_task_focus: "/tarefasExpansao",
    engagement_job_opportunities: "/ad-link/vagas",
    engagement_saved_talents: "/meus-salvos",
    engagement_achievement_profile_complete: "/ad-link/curriculo",
  };

  return fallbackByEvent[event || ""] || "/notificacoes?type=engajamento";
}

function openInNewTab(destination: string) {
  const target =
    destination.startsWith("http://") || destination.startsWith("https://")
      ? destination
      : `${window.location.origin}${destination.startsWith("/") ? destination : `/${destination}`}`;
  const opened = window.open(target, "_blank", "noopener,noreferrer");
  if (opened) opened.opener = null;
}

function updateReadState<T>(value: T, ids: Set<number>): T {
  if (!value) return value;

  if (Array.isArray(value)) {
    return value.map((notification: NotificationItem) =>
      ids.has(notification.id) ? { ...notification, read: true } : notification,
    ) as T;
  }

  const response = value as NotificationApiResponse & InfiniteNotificationData;

  if (Array.isArray(response.data?.data)) {
    return {
      ...response,
      data: {
        ...response.data,
        data: updateReadState(response.data.data, ids),
      },
    } as T;
  }

  if (Array.isArray(response.pages)) {
    return {
      ...response,
      pages: response.pages.map((page) => updateReadState(page, ids)),
    } as T;
  }

  return value;
}

function inferTitleFromDescription(description?: string | null) {
  if (!description) return "";

  const projectStatus = description.match(/^O projeto\s+(.+?)\s+está/i);
  if (projectStatus?.[1]) return projectStatus[1].trim();

  const projectComment = description.match(/comentou no projeto\s+(.+)$/i);
  if (projectComment?.[1]) return projectComment[1].trim();

  const application = description.match(/vaga\s+"([^"]+)"/i);
  if (application?.[1]) return application[1].trim();

  return "";
}

function normalizeLookupText(value?: string | null) {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function asEntityId(value: unknown) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const normalizedValue = String(value).trim();
  return /^\d+$/.test(normalizedValue) ? normalizedValue : null;
}

function getTypeFilterFromSearch(search: string): NotifType {
  const type = new URLSearchParams(search).get("type");
  return NOTIFICATION_TYPES.includes(type as NotifType) ? (type as NotifType) : "all";
}

function isDeskSharedEvent(event?: string | null) {
  return !!event?.includes("projeto_shared");
}

function isDeskStatusEvent(event?: string | null) {
  return event === "status_projeto_shared";
}

function isDeskCommentEvent(event?: string | null) {
  return event === "comentario_projeto_shared" || event === "comentario_addesk";
}

function getEntityIdFromObject(
  value: Record<string, unknown>,
  keys: string[],
) {
  for (const key of keys) {
    const entityId = asEntityId(value[key]);
    if (entityId) return entityId;
  }

  return null;
}

function getNotificationDeskId(
  notification: NotificationItem,
  deskIdByTitle?: Map<string, number | string>,
) {
  const directDeskId = notification?.addesk?.id;
  if (directDeskId) return directDeskId;

  const parsedLink = parseNotificationLink(notification?.link);
  const linkDeskId = asEntityId(parsedLink);
  if (isDeskSharedEvent(notification?.event) && linkDeskId) return linkDeskId;
  if (parsedLink && typeof parsedLink === "object") {
    const objectDeskId = getEntityIdFromObject(
      parsedLink as Record<string, unknown>,
      ["desk_id", "addesk_id", "id_desk", "id_addesk", "projeto_id", "id"],
    );
    if (objectDeskId) return objectDeskId;
  }

  const inferredTitle = normalizeLookupText(inferTitleFromDescription(notification?.description));
  return inferredTitle ? deskIdByTitle?.get(inferredTitle) ?? null : null;
}

function getNotificationTaskId(notification?: NotificationItem | null) {
  const directTaskId = notification?.tarefa?.id;
  if (directTaskId) return directTaskId;

  const parsedLink = parseNotificationLink(notification?.link);
  const directLinkId = asEntityId(parsedLink);
  if (directLinkId) return directLinkId;

  if (parsedLink && typeof parsedLink === "object") {
    const linkObject = parsedLink as Record<string, unknown>;
    return (
      asEntityId(linkObject.tarefa_id) ??
      asEntityId(linkObject.id_tarefa) ??
      asEntityId(linkObject.task_id) ??
      asEntityId(linkObject.id)
    );
  }

  return null;
}

function categoryFromNotification(notification: NotificationItem): CategoryKey {
  const event = notification?.event ?? "";

  if (isTaskCommentNotificationEvent(event)) return "tarefa_comentario";
  if (isTaskNotificationEvent(event)) return "tarefa";
  if (event.includes("reuniao")) return "reuniao";
  if (isDeskStatusEvent(event)) return "desk_status";
  if (isDeskCommentEvent(event)) return "desk_comment";
  if (notification?.addesk || isDeskSharedEvent(event)) return "addesk";
  if (
    event.startsWith("vaga_") ||
    event === "vaga_nova_candidatura" ||
    event.includes("candidatura") ||
    isTalentEvent(event)
  ) {
    return "recrutamento";
  }
  if (event.startsWith("feedback_")) return "feedback";
  if (event.startsWith("social_")) return "social";
  if (isEngagementEvent(event)) return "engajamento";
  if (event.includes("_shared")) return "shared";
  return "addesk";
}

function categoryLabel(
  type: CategoryKey,
  t: (key: string) => string,
  notification?: NotificationItem,
) {
  const labels: Record<CategoryKey, string> = {
    tarefa: taskNotificationLabel(notification?.event, t),
    tarefa_comentario: t("notificationsModule.badges.comments"),
    reuniao: t("notificationsModule.filters.type_meeting"),
    recrutamento: "Recrutamento",
    feedback: "Feedback",
    social: "Rede Social",
    engajamento: "Engajamento",
    shared: "Compartilhado",
    desk_status: "Status Desk",
    desk_comment: "Comentário Desk",
    addesk: t("notificationsModule.filters.type_desk"),
  };

  return labels[type];
}

function categoryClasses(type: CategoryKey) {
  const classes: Record<CategoryKey, string> = {
    tarefa:
      "bg-goon-amareloClaro text-goon-amarelo border-goon-amareloClaro",
    tarefa_comentario:
      "bg-goon-amareloClaro text-goon-amarelo border-goon-amareloClaro",
    reuniao: "bg-azul-400/10 text-azul-400 border-azul-400/20",
    recrutamento: "bg-verde-300/15 text-cinza-800 border-verde-300/40",
    feedback: "bg-cinza-100 text-cinza-800 border-cinza-200",
    social: "bg-azul-400 text-white border-azul-400",
    engajamento: "bg-azul-400/10 text-azul-400 border-azul-400/20",
    shared: "bg-cinza-800 text-white border-cinza-800",
    desk_status: "bg-[#153DCE] text-white border-[#153DCE]",
    desk_comment: "bg-azul-400 text-white border-azul-400",
    addesk: "bg-azul-400 text-white border-azul-400",
  };

  return classes[type];
}

function CategoryIcon({ type }: { type: CategoryKey }) {
  const className = "w-4 h-4";
  if (type === "tarefa") return <CheckCheck className={className} />;
  if (type === "tarefa_comentario") return <MessageCircle className={className} />;
  if (type === "reuniao") return <CalendarDays className={className} />;
  if (type === "recrutamento") return <BriefcaseBusiness className={className} />;
  if (type === "feedback") return <Flag className={className} />;
  if (type === "social") return <Users className={className} />;
  if (type === "engajamento") return <Sparkles className={className} />;
  if (type === "shared") return <Share2 className={className} />;
  if (type === "desk_status") return <CircleDot className={className} />;
  if (type === "desk_comment") return <MessageCircle className={className} />;
  return <Bell className={className} />;
}

function getNotificationTitle(item: GroupedNotificationItem, t: (key: string) => string) {
  const { type, tarefa, addesk, feedbackReport, post, profileUser, lastNotification } = item;
  const inferredTitle = inferTitleFromDescription(lastNotification?.description);

  if (isTaskCategory(type)) {
    return tarefa?.title ?? (inferredTitle || t("notificationsModule.fallbacks.task_no_title"));
  }
  if (type === "reuniao") return t("notificationsModule.filters.type_meeting");
  if (type === "recrutamento") return inferredTitle || "Recrutamento";
  if (type === "feedback") return feedbackReport?.title ?? (inferredTitle || "Feedback");
  if (type === "social") {
    if (isSocialProfileEvent(lastNotification?.event)) {
      return profileUser
        ? `${profileUser?.name ?? ""} ${profileUser?.last_name ?? ""}`.trim()
        : "Rede Social";
    }
    return post?.creator
      ? `${post?.creator?.name ?? ""} ${post?.creator?.last_name ?? ""}`.trim()
      : "Publicação";
  }
  if (type === "engajamento") return inferredTitle || "Progresso na plataforma";
  return addesk?.name ?? (inferredTitle || t("notificationsModule.fallbacks.addesk_no_name"));
}

function resolveDestination(item: GroupedNotificationItem, userLogged: UserLogged) {
  const { type, tarefa, addesk, feedbackReport, post, profileUser, lastNotification } = item;
  const eventName = lastNotification?.event ?? "";
  const linkValue = parseNotificationLink(lastNotification?.link);
  const linkParam = asUrlParam(linkValue);

  if (isTaskCategory(type)) {
    const tarefaId = tarefa?.id ?? getNotificationTaskId(lastNotification);
    return `/tarefasExpansao${tarefaId ? `?tarefa_id=${asUrlParam(tarefaId)}&comentarios=1` : ""}`;
  }

  if (type === "reuniao") {
    return `/reunioesExpansao${linkParam ? `?reuniao_id=${linkParam}` : ""}`;
  }

  if (type === "recrutamento") {
    if (eventName === "vaga_publicada_candidato" || eventName === "talent_invite_job") {
      return `/ad-link/vagas${linkParam ? `?vaga_id=${linkParam}` : ""}`;
    }
    if (eventName === "vaga_nova_candidatura") {
      const candidaturaId = item?.lastNotification?.candidatura?.id ?? linkValue;
      const vagaId = item?.lastNotification?.vaga?.id;
      const params = new URLSearchParams();
      if (candidaturaId) params.set("candidatura_id", String(candidaturaId));
      if (vagaId) params.set("vaga_id", String(vagaId));
      return `/dash-recrutamento${params.toString() ? `?${params.toString()}` : ""}`;
    }
    if (eventName === "talent_message") {
      return linkParam ? `/chat-expandido/${linkParam}` : "/notificacoes?type=recrutamento";
    }
    if (eventName === "talent_saved") {
      return linkParam ? `/ad-link/perfil/${linkParam}` : "/meus-salvos";
    }
    if (eventName === "professional_invite_accepted") {
      return linkParam
        ? `/ad-link/perfil/${linkParam}`
        : "/notificacoes?type=recrutamento";
    }
    if (eventName === "talent_invite_project") return "/notificacoes?type=recrutamento";
    if (userLogged?.data?.is_ad_link && eventName.includes("candidatura")) {
      return `/ad-link/minhas-candidaturas${linkParam ? `?candidatura_id=${linkParam}` : ""}`;
    }

    const candidaturaId =
      item?.lastNotification?.candidatura?.id ?? (eventName.includes("candidatura") ? linkValue : "");
    const vagaId = item?.lastNotification?.vaga?.id ?? (eventName.includes("vaga") ? linkValue : "");
    const params = new URLSearchParams();
    if (candidaturaId) params.set("candidatura_id", String(candidaturaId));
    if (vagaId) params.set("vaga_id", String(vagaId));
    return `/dash-recrutamento${params.toString() ? `?${params.toString()}` : ""}`;
  }

  if (type === "feedback") {
    const reportId = feedbackReport?.id ?? linkValue;
    const basePath = userLogged?.data?.is_admin ? "/admin/feedback" : "/feedback";
    return reportId ? `${basePath}/${asUrlParam(reportId)}` : basePath;
  }

  if (type === "social") {
    if (isSocialProfileEvent(eventName)) {
      const profileUserId = profileUser?.id ?? linkValue;
      return profileUserId ? `/ad-link/perfil/${asUrlParam(profileUserId)}` : "/ad-link";
    }
    const postId = post?.id ?? linkValue;
    return `/ad-link${postId ? `?post_id=${asUrlParam(postId)}` : ""}`;
  }

  if (type === "engajamento") {
    return getEngagementDestination(lastNotification?.event, lastNotification?.link);
  }

  const deskId = item.resolvedDeskId ?? addesk?.id ?? asEntityId(linkValue);
  if (type === "desk_status") {
    return deskId ? `/desk/${asUrlParam(deskId)}` : "/notificacoes?type=desk";
  }
  if (type === "desk_comment") {
    return deskId
      ? `/desks/${asUrlParam(deskId)}?tab=chat`
      : "/notificacoes?type=desk";
  }
  if (deskId) return `/desks/${asUrlParam(deskId)}?tab=chat`;
  if (type === "shared") return "/notificacoes?type=shared";
  return "/notificacoes?type=desk";
}

export function Notificacoes() {
  const { t } = useTranslation();
  const location = useLocation();
  const { notifications, setNotifications, userLogged } = useLayout();
  const [filterUser, setFilterUser] = useState<SelectOption | null>(null);
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<NotifType>(() =>
    getTypeFilterFromSearch(location.search),
  );
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<TaskNotificationTarget | null>(null);
  const [loadingTaskId, setLoadingTaskId] = useState<number | string | null>(null);
  const [loadingTaskTitle, setLoadingTaskTitle] = useState<string>("");
  const queryClient = useQueryClient();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const taskRequestRef = useRef(0);

  const {
    data,
    isLoading: localLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["notificacoes-paginadas", typeFilter, unreadOnly],
    queryFn: async ({ pageParam = 1 }) =>
      (await NotificationsService.get({
        page: pageParam as number,
        perPage: PER_PAGE,
        type: typeFilter !== "all" ? typeFilter : undefined,
        unread: unreadOnly || undefined,
      })) as NotificationApiResponse,
    getNextPageParam: (lastPage: NotificationApiResponse, allPages) => {
      const total = lastPage?.data?.qtdRegistros ?? 0;
      const loaded = allPages.length * PER_PAGE;
      return loaded < total ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
  });

  const localNotifications: NotificationItem[] = useMemo(
    () => data?.pages.flatMap((page) => page?.data?.data ?? []) ?? [],
    [data],
  );
  const isStandalonePage = location.pathname === "/notificacoes";

  const handleObserverRef = useRef<
    (entries: IntersectionObserverEntry[]) => void
  >(() => {});

  useLayoutEffect(() => {
    handleObserverRef.current = (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
  });

  useEffect(() => {
    const element = sentinelRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => handleObserverRef.current(entries),
      { root: null, rootMargin: "100px", threshold: 0 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setTypeFilter(getTypeFilterFromSearch(location.search));
    setFilterUser(null);
  }, [location.search]);

  function handleTypeFilter(type: NotifType) {
    setTypeFilter(type);
    setFilterUser(null);
  }

  async function markNotificationsAsRead(notificationIds: number[]) {
    const ids = new Set(notificationIds.filter(Boolean));
    if (!ids.size) return;

    setNotifications((prevNotifications: unknown) => updateReadState(prevNotifications, ids));
    queryClient.setQueriesData({ queryKey: ["notificacoes-paginadas"] }, (oldData: unknown) =>
      updateReadState(oldData, ids),
    );
    queryClient.setQueriesData({ queryKey: ["dataNotificacoes"] }, (oldData: unknown) =>
      updateReadState(oldData, ids),
    );

    try {
      await Promise.all([...ids].map((id) => NotificationsService.read(id)));
    } catch (error) {
      console.error(t("notificationsModule.errors.mark_task_read"), error);
    } finally {
      queryClient.invalidateQueries({ queryKey: ["notificacoes-paginadas"] });
      queryClient.invalidateQueries({ queryKey: ["dataNotificacoes"] });
    }
  }

  async function viewAllNotifications() {
    const ids = new Set(localNotifications.map((notification) => notification?.id).filter(Boolean));

    setNotifications((prevNotifications: unknown) => updateReadState(prevNotifications, ids));
    queryClient.setQueriesData({ queryKey: ["notificacoes-paginadas"] }, (oldData: unknown) =>
      updateReadState(oldData, ids),
    );
    queryClient.setQueriesData({ queryKey: ["dataNotificacoes"] }, (oldData: unknown) =>
      updateReadState(oldData, ids),
    );

    try {
      await NotificationsService.readAll();
    } catch (error) {
      console.error(t("notificationsModule.errors.mark_all_read"), error);
    } finally {
      queryClient.invalidateQueries({ queryKey: ["notificacoes-paginadas"] });
      queryClient.invalidateQueries({ queryKey: ["dataNotificacoes"] });
    }
  }

  async function openTaskFromNotification(item: GroupedNotificationItem) {
    const taskId = getNotificationTaskId(item.lastNotification);
    if (!taskId) {
      toast.error("Não foi possível identificar a tarefa desta notificação.");
      return;
    }

    const requestId = taskRequestRef.current + 1;
    taskRequestRef.current = requestId;
    setSelectedTask(null);
    setLoadingTaskId(taskId);
    setLoadingTaskTitle(getNotificationTitle(item, t));
    try {
      const response = await ServiceResumoDesk.getTaskById(taskId);
      const task = response?.data?.data ?? response?.data;
      if (taskRequestRef.current !== requestId) return;

      if (response?.status === 200 && task?.id) {
        setSelectedTask(task);
      } else {
        toast.error("Não foi possível carregar a tarefa.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível carregar a tarefa.");
    } finally {
      if (taskRequestRef.current === requestId) {
        setLoadingTaskId(null);
        setLoadingTaskTitle("");
      }
    }
  }

  function closeTaskModal() {
    taskRequestRef.current += 1;
    setSelectedTask(null);
    setLoadingTaskId(null);
    setLoadingTaskTitle("");
  }

  function reloadSelectedTask() {
    if (!selectedTask?.id) return;
    void ServiceResumoDesk.getTaskById(selectedTask.id).then((response) => {
      const task = response?.data?.data ?? response?.data;
      if (response?.status === 200 && task?.id) {
        setSelectedTask(task);
        return;
      }
      setSelectedTask(null);
    });
  }

  function handleEditTaskFromNotification(task: TaskNotificationTarget) {
    setSelectedTask(null);
    openInNewTab(`/tarefasExpansao?tarefa_id=${asUrlParam(task?.id)}&comentarios=1`);
  }

  function handleNotificationClick(item: GroupedNotificationItem) {
    if (isTaskCategory(item.type)) {
      void openTaskFromNotification(item);
    } else {
      const destination = resolveDestination(item, userLogged);
      openInNewTab(destination);
    }

    const unreadIds = groupedNotifications[item.itemId]
      ?.filter((notification: NotificationItem) => !notification?.read)
      .map((notification: NotificationItem) => notification.id);

    void markNotificationsAsRead(unreadIds || []);
  }

  const uniqueUsers = useMemo(
    () => {
      const users = new Map<number | string, UserPreview>();
      localNotifications.forEach((notification) => {
        const sender = notification.usuario_send;
        if (sender?.id) users.set(sender.id, sender);
      });
      return [...users.values()];
    },
    [localNotifications],
  );

  const userOptions = useMemo<SelectOption[]>(
    () =>
      uniqueUsers.map((item) => ({
        value: item.id!,
        label: `${item?.name ?? ""} ${item?.last_name ?? ""}`.trim() || "Usuário",
      })),
    [uniqueUsers],
  );

  const deskIdByTitle = useMemo(() => {
    const deskMap = new Map<string, number | string>();
    localNotifications.forEach((notification) => {
      const deskId =
        notification?.addesk?.id ??
        (isDeskSharedEvent(notification?.event)
          ? asEntityId(parseNotificationLink(notification?.link))
          : null);
      if (!deskId) return;

      const titles = [
        notification?.addesk?.name,
        inferTitleFromDescription(notification?.description),
      ];

      titles.forEach((title) => {
        const normalizedTitle = normalizeLookupText(title);
        if (normalizedTitle && !deskMap.has(normalizedTitle)) {
          deskMap.set(normalizedTitle, deskId);
        }
      });
    });
    return deskMap;
  }, [localNotifications]);

  const groupedNotifications = useMemo(
    () =>
      localNotifications.reduce<NotificationGroups>((acc, notification) => {
        const category = categoryFromNotification(notification);
        const notificationLink = parseNotificationLink(notification?.link);
        const tarefaId = isTaskCategory(category) ? getNotificationTaskId(notification) : null;
        const addeskId = getNotificationDeskId(notification, deskIdByTitle);
        const feedbackReportId =
          notification?.feedback_report?.id ??
          (notification?.event?.startsWith("feedback_") ? notificationLink : null);
        const socialPostId =
          category === "social" && !isSocialProfileEvent(notification?.event)
            ? (notification?.post?.id ?? notificationLink)
            : null;
        const socialProfileId =
          category === "social" && isSocialProfileEvent(notification?.event)
            ? (notification?.profile_user?.id ?? notificationLink)
            : null;

        const itemId = tarefaId
          ? `tarefa-${tarefaId}`
          : addeskId
            ? `${category}-${addeskId}`
            : category === "reuniao"
              ? `reuniao-${notificationLink ?? notification.id}`
              : category === "recrutamento"
                ? `recrutamento-${notification?.candidatura?.id ?? notification?.link ?? notification.id}`
                : feedbackReportId
                  ? `feedback-${feedbackReportId}`
                  : socialPostId
                    ? `social-post-${socialPostId}`
                    : socialProfileId
                      ? `social-user-${socialProfileId}`
                      : category === "engajamento"
                        ? `engajamento-${notification?.event ?? notification.id}-${notification?.link ?? notification.id}`
                        : `${category}-${notification?.link ?? notification.id}`;

        if (!acc[itemId]) {
          acc[itemId] = [];
        }
        acc[itemId].push(notification);
        return acc;
      }, {}),
    [deskIdByTitle, localNotifications],
  );

  const groupedNotificationsArray = useMemo<GroupedNotificationItem[]>(
    () =>
      Object.keys(groupedNotifications)
        .map((itemId) => {
          const itemNotifications = groupedNotifications[itemId];
          const sortedNotifications = [...itemNotifications].sort((a, b) => {
            const dateA = new Date(a.created_at || 0).getTime();
            const dateB = new Date(b.created_at || 0).getTime();
            return dateA - dateB;
          });

          const lastNotification = sortedNotifications[sortedNotifications.length - 1];
          const type = categoryFromNotification(lastNotification);

          return {
            itemId,
            tarefa: lastNotification?.tarefa,
            addesk: lastNotification?.addesk,
            resolvedDeskId: getNotificationDeskId(lastNotification, deskIdByTitle),
            feedbackReport: lastNotification?.feedback_report,
            post: lastNotification?.post,
            profileUser: lastNotification?.profile_user,
            lastNotification,
            type,
          };
        })
        .sort((a, b) => {
          const dateA = new Date(a.lastNotification?.created_at || 0).getTime();
          const dateB = new Date(b.lastNotification?.created_at || 0).getTime();
          return dateB - dateA;
        }),
    [deskIdByTitle, groupedNotifications],
  );

  const filterUserNotification = filterUser?.value
    ? groupedNotificationsArray.filter(
        (item: GroupedNotificationItem) =>
          item?.lastNotification?.usuario_send?.id === filterUser?.value,
      )
    : groupedNotificationsArray;

  const unreadTotal = Array.isArray(notifications)
    ? (notifications as NotificationItem[]).filter((notification) => !notification?.read).length
    : 0;

  const visibleUnreadTotal = localNotifications.filter(
    (notification) => !notification?.read,
  ).length;
  const hasActiveFilters = unreadOnly || typeFilter !== "all" || !!filterUser;
  const pulseProgress = Math.min(
    100,
    Math.max(8, visibleUnreadTotal ? visibleUnreadTotal * 12 : 100),
  );

  const TYPE_FILTERS: { value: NotifType; label: string }[] = [
    { value: "all", label: t("notificationsModule.filters.type_all") },
    { value: "desk", label: t("notificationsModule.filters.type_desk") },
    { value: "reuniao", label: t("notificationsModule.filters.type_meeting") },
    { value: "shared", label: t("notificationsModule.filters.type_shared") },
    { value: "recrutamento", label: "Recrutamento" },
    { value: "feedback", label: "Feedback" },
    { value: "social", label: "Rede Social" },
    { value: "engajamento", label: "Engajamento" },
  ];
  const unreadOnlyLabel = t("notificationsModule.filters.unread_only");
  const containerClassName = isStandalonePage
    ? "mx-auto flex min-h-[calc(100vh-6rem)] w-full max-w-6xl flex-col gap-4 px-4 py-6 text-cinza-800 sm:px-6"
    : "flex h-full min-h-0 flex-col text-cinza-800";
  const headerClassName = isStandalonePage
    ? "rounded-2xl border border-cinza-200 bg-white p-5 shadow-sm"
    : "sticky top-0 z-10 bg-white/95 pb-4 pt-6 backdrop-blur";
  const listClassName = isStandalonePage
    ? "grid gap-3 pb-8"
    : "min-h-0 flex-1 space-y-3 overflow-y-auto pb-4 pr-1";
  const notificationCardClassName = (isUnread: boolean) =>
    `group relative w-full rounded-lg border text-left transition-all ${
      isStandalonePage ? "p-4" : "p-3"
    } ${
      isUnread
        ? "border-azul-300 bg-azul-400/5 shadow-sm"
        : "border-cinza-200 bg-white hover:border-azul-300"
    }`;

  return (
    <div className={containerClassName}>
      <section className={headerClassName}>
        <div
          className={`flex items-start justify-between gap-3 ${
            isStandalonePage ? "" : "pr-14"
          }`}
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-azul-400 text-white">
                <Bell className="h-4 w-4" />
              </span>
              <div>
                <h2 className="text-xl font-semibold leading-tight text-cinza-800">
                  {t("notificationsModule.title")}
                </h2>
                <p className="text-xs text-cinza-500">
                  {unreadTotal} {t("notificationsModule.unread_count")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersExpanded((prev) => !prev)}
              title="Filtros"
              className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all ${
                filtersExpanded || hasActiveFilters
                  ? "border-azul-400 bg-azul-400 text-white"
                  : "border-cinza-200 text-cinza-500 hover:border-azul-400 hover:text-azul-400"
              }`}
            >
              <ListFilter className="h-4 w-4" />
            </button>
          </div>
        </div>

        {visibleUnreadTotal > 0 && (
          <div className={`${isStandalonePage ? "" : "pr-14"} mt-3 flex justify-end`}>
            <button
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border border-azul-400 px-3 text-xs font-medium text-azul-400 transition-all hover:bg-azul-400 hover:text-white"
              type="button"
              onClick={viewAllNotifications}
            >
              <CheckCheck className="h-4 w-4" />
              {t("notificationsModule.buttons.mark_all_read")}
            </button>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 text-[11px] text-cinza-500">
          <span className="shrink-0 font-medium text-cinza-700">Pulso</span>
          <div className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-cinza-100">
            <div
              className="h-full rounded-full bg-azul-400 transition-all"
              style={{
                width: `${pulseProgress}%`,
              }}
            />
          </div>
          <span className="shrink-0">
            {visibleUnreadTotal > 0 ? `${visibleUnreadTotal} novas` : "Tudo em dia"}
          </span>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setFiltersExpanded((prev) => !prev)}
            className="inline-flex items-center gap-1.5 rounded-full border border-cinza-200 bg-white px-3 py-1 text-xs font-medium text-cinza-600 transition-all hover:border-azul-300 hover:text-azul-400"
          >
            Filtros
            {hasActiveFilters && <span className="h-1.5 w-1.5 rounded-full bg-azul-400" />}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${
                filtersExpanded ? "rotate-180" : ""
              }`}
            />
          </button>

          {!filtersExpanded && hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setTypeFilter("all");
                setUnreadOnly(false);
                setFilterUser(null);
              }}
              className="text-xs font-medium text-azul-400 hover:text-azul-500"
            >
              Limpar
            </button>
          )}
        </div>

        {filtersExpanded && (
          <div className="mt-3 rounded-lg border border-cinza-200 bg-white p-3">
            <div className="mb-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  setUnreadOnly((prev) => !prev);
                  setFilterUser(null);
                }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                  unreadOnly
                    ? "border-azul-400 bg-azul-400 text-white"
                    : "border-cinza-200 bg-cinza-50 text-cinza-600 hover:border-azul-300 hover:text-azul-400"
                }`}
              >
                <ListFilter className="h-3.5 w-3.5" />
                {unreadOnlyLabel}
              </button>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    setTypeFilter("all");
                    setUnreadOnly(false);
                    setFilterUser(null);
                  }}
                  className="text-xs font-medium text-azul-400 hover:text-azul-500"
                >
                  Limpar filtros
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-1.5">
              {TYPE_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleTypeFilter(filter.value)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                    typeFilter === filter.value
                      ? "border-azul-400 bg-azul-400 text-white shadow-sm"
                      : "border-cinza-200 bg-white text-cinza-500 hover:border-azul-300 hover:text-azul-400"
                  }`}
                  type="button"
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <label className="mt-3 flex w-full max-w-[244px] flex-col gap-1">
              <span className="pl-1 text-xs font-medium text-cinza-500">
                {t("notificationsModule.filters.filter_by_user")}
              </span>
              <Select<SelectOption, false>
                className="text-xs"
                onChange={(value: SingleValue<SelectOption>) => setFilterUser(value ?? null)}
                placeholder={t("notificationsModule.filters.select_user")}
                isClearable
                value={filterUser}
                options={userOptions}
              />
            </label>
          </div>
        )}
      </section>

      <section className={listClassName}>
        {localLoading ? (
          <div className="rounded-lg border border-cinza-200 bg-cinza-100 px-4 py-8 text-center text-xs text-cinza-500">
            {t("common:loading")}
          </div>
        ) : filterUserNotification.length === 0 ? (
          <div className="rounded-lg border border-dashed border-cinza-200 bg-white px-4 py-8 text-center text-xs text-cinza-500">
            {t("notificationsModule.empty")}
          </div>
        ) : (
          filterUserNotification.map((item: GroupedNotificationItem) => {
            const unreadCount =
              groupedNotifications[item.itemId]?.filter((notification) => !notification?.read)
                ?.length || 0;
            const isUnread = unreadCount > 0;
            const title = getNotificationTitle(item, t);
            const label = categoryLabel(item.type, t, item.lastNotification);
            const sender = item.lastNotification?.usuario_send;
            const destination = resolveDestination(item, userLogged);
            const createdAt = item.lastNotification?.created_at || new Date().toISOString();
            const taskId = getNotificationTaskId(item.lastNotification);
            const isLoadingThisTask = isTaskCategory(item.type) && loadingTaskId === taskId;

            return (
              <button
                className={notificationCardClassName(isUnread)}
                key={item.itemId}
                onClick={() => handleNotificationClick(item)}
                disabled={isLoadingThisTask}
                type="button"
              >
                <div className="flex gap-3">
                  <div
                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${categoryClasses(
                      item.type,
                    )}`}
                  >
                    <CategoryIcon type={item.type} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${categoryClasses(
                              item.type,
                            )}`}
                          >
                            {label}
                          </span>
                        </div>

                        <h3
                          className={`mt-1 line-clamp-2 text-sm leading-snug ${
                            isUnread ? "font-semibold text-cinza-800" : "font-medium text-cinza-800"
                          }`}
                          title={title}
                        >
                          {title}
                        </h3>
                      </div>

                      {unreadCount > 1 && (
                        <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
                          {unreadCount}
                        </span>
                      )}
                    </div>

                    <p
                      title={item.lastNotification?.description ?? undefined}
                      className="mt-1 line-clamp-2 whitespace-pre-line text-xs leading-relaxed text-cinza-500"
                    >
                      {item.lastNotification?.description || "Notificação recebida."}
                    </p>

                    <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                      <div className="flex min-w-0 items-center gap-2">
                        {sender?.url_img?.path ? (
                          <img
                            src={sender.url_img.path}
                            alt=""
                            className="block h-6 w-6 min-w-[1.5rem] max-w-[1.5rem] shrink-0 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-6 w-6 min-w-[1.5rem] max-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-cinza-100 text-cinza-500">
                            <UserRound className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <span className="truncate font-medium text-cinza-800">
                          {sender
                            ? `${sender?.name ?? ""} ${sender?.last_name ?? ""}`.trim()
                            : "Ad Desk"}
                        </span>
                      </div>

                      <div className="flex shrink-0 items-center gap-2 text-cinza-500">
                        <span title={moment(createdAt).format("DD/MM/YYYY - HH:mm")}>
                          {moment(createdAt).fromNow()}
                        </span>
                        <span
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-cinza-200 bg-white text-azul-400 transition-all group-hover:border-azul-400 group-hover:bg-azul-400 group-hover:text-white"
                          title={isTaskCategory(item.type) ? "Abrir tarefa" : destination}
                        >
                          {isTaskCategory(item.type) ? (
                            <CheckCheck className="h-3.5 w-3.5" />
                          ) : (
                            <ExternalLink className="h-3.5 w-3.5" />
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}

        <div ref={sentinelRef} className="py-2">
          {isFetchingNextPage && (
            <div className="rounded-lg border border-cinza-200 bg-cinza-100 py-3 text-center text-xs text-cinza-500">
              {t("common:loading")}
            </div>
          )}
        </div>
      </section>
      {(selectedTask || loadingTaskId) && (
        <TarefaView
          task={selectedTask}
          openEdit={handleEditTaskFromNotification}
          getTasks={reloadSelectedTask}
          setTaskSelected={closeTaskModal}
          isLoading={!!loadingTaskId && !selectedTask}
          loadingTitle={loadingTaskTitle}
        />
      )}
    </div>
  );
}
